import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';

/**
 * Public page showing SMS opt-in consent UI for Twilio compliance review
 * This page is accessible without authentication
 * Matches the admin notification preferences modal UI
 */
export function SmsOptInPublicPage() {
  const [phoneInput, setPhoneInput] = useState('+16318061185');
  const [smsOptedIn, setSmsOptedIn] = useState(false);
  
  // Demo notification preferences (matching screenshot)
  const [preferences] = useState([
    { type: 'Frames Pending', email: true, sms: true, threshold: 7 },
    { type: 'Frames Ordered Not Received', email: true, sms: false, threshold: 7 },
    { type: 'Location Inactive', email: true, sms: false, threshold: 7 },
  ]);

  const hasPhoneNumber = phoneInput && phoneInput.length >= 12;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 py-4 flex justify-between items-center gap-4">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Frame Replenishment System</h1>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <span className="text-xs sm:text-sm text-gray-600 truncate max-w-[120px] sm:max-w-none">Admin</span>
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-4rem)]">
        {/* Sidebar Navigation */}
        <aside className="w-64 bg-white border-r border-gray-200 h-[calc(100vh-4rem)] flex-shrink-0">
          <nav className="p-4 space-y-1 h-full overflow-y-auto">
            <div className="block px-4 py-2 rounded-lg text-sm font-medium text-gray-700">Dashboard</div>
            <div className="block px-4 py-2 rounded-lg text-sm font-medium text-gray-700">Frames to Order</div>
            <div className="block px-4 py-2 rounded-lg text-sm font-medium text-gray-700">Weekly Ordering</div>
            <div className="block px-4 py-2 rounded-lg text-sm font-medium text-gray-700">Receiving</div>
            <div className="block px-4 py-2 rounded-lg text-sm font-medium text-gray-700">Reports</div>
            <div className="block px-4 py-2 rounded-lg text-sm font-medium bg-blue-50 text-blue-700">Admin</div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-x-auto">
          <div className="p-4 sm:p-6 max-w-4xl">
            {/* Modal-like Card Container */}
            <Card className="p-6 max-w-3xl mx-auto">
              <h1 className="text-xl font-semibold text-gray-900 mb-6">
                Notification Preferences: kbum1013
              </h1>

              <div className="space-y-6">
                {/* User Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm">
                    <p><span className="font-medium">Email:</span> kbum1013@aol.com</p>
                    <p className="mt-1">
                      <span className="font-medium">Locations:</span> MAEC, CEA, EEC
                    </p>
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Phone Number</h3>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 max-w-xs">
                      <Input
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {}}
                      disabled
                    >
                      Save
                    </Button>
                  </div>

                  {/* SMS Consent Checkbox */}
                  {hasPhoneNumber && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={smsOptedIn}
                          onChange={(e) => setSmsOptedIn(e.target.checked)}
                          className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <div className="flex-1">
                          <span className="text-sm text-gray-900">
                            I agree to receive SMS text messages from Optical Management System for operational alerts, reminders, and account notifications. Message frequency varies. Reply STOP to opt out.
                          </span>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                {/* Preferences Table */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Notification Preferences</h3>
                  
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 font-medium text-gray-700">Type</th>
                        <th className="text-center py-2 font-medium text-gray-700">Email</th>
                        <th className="text-center py-2 font-medium text-gray-700">SMS</th>
                        <th className="text-center py-2 font-medium text-gray-700">Threshold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preferences.map((pref, index) => (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="py-3">{pref.type}</td>
                          <td className="py-3 text-center">
                            <input
                              type="checkbox"
                              checked={pref.email}
                              disabled
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-3 text-center">
                            <input
                              type="checkbox"
                              checked={pref.sms}
                              disabled={!hasPhoneNumber || !smsOptedIn}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                            />
                          </td>
                          <td className="py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <input
                                type="number"
                                min="1"
                                max="365"
                                value={pref.threshold}
                                disabled
                                className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              />
                              <span className="text-gray-500">days</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="text-xs text-gray-500 bg-gray-50 rounded p-3">
                  <p><strong>Note:</strong> Global default thresholds are set in the Notifications tab. 
                  Users can override these thresholds. A "custom" badge indicates a user override.</p>
                </div>

                {/* Close Button */}
                <div className="flex justify-end pt-2 border-t">
                  <Button variant="secondary" disabled>
                    Close
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
